import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, History, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminActivityLogsPage() {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [actionFilter, setActionFilter] = useState("all");

    const token = localStorage.getItem('superadmin_token');

    useEffect(() => {
        if (!token) {
            navigate('/superadmin/login');
            return;
        }
        loadLogs();
    }, []);

    useEffect(() => {
        filterLogs();
    }, [searchTerm, actionFilter, logs]);

    const loadLogs = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/superadmin/logs/activity?limit=200`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLogs(response.data);
        } catch (error) {
            toast.error("Failed to load activity logs");
        } finally {
            setLoading(false);
        }
    };

    const filterLogs = () => {
        let filtered = logs;

        if (actionFilter !== "all") {
            filtered = filtered.filter(log => log.action === actionFilter);
        }

        if (searchTerm) {
            filtered = filtered.filter(log =>
                log.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                log.admin_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (log.salon_name && log.salon_name.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        setFilteredLogs(filtered);
    };

    const getActionColor = (action) => {
        const colors = {
            'admin_login': 'bg-blue-100 text-blue-800',
            'staff_added': 'bg-green-100 text-green-800',
            'staff_updated': 'bg-yellow-100 text-yellow-800',
            'staff_deleted': 'bg-red-100 text-red-800',
            'service_added': 'bg-green-100 text-green-800',
            'service_updated': 'bg-yellow-100 text-yellow-800',
            'service_deleted': 'bg-red-100 text-red-800',
            'salon_updated': 'bg-purple-100 text-purple-800',
            'subscription_updated': 'bg-indigo-100 text-indigo-800',
        };
        return colors[action] || 'bg-gray-100 text-gray-800';
    };

    const getActionIcon = (action) => {
        return <History className="w-4 h-4" />;
    };

    const uniqueActions = [...new Set(logs.map(log => log.action))];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/superadmin')}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>
                    <div>
                        <h1 className="font-heading text-3xl font-bold">Activity Logs</h1>
                        <p className="text-muted-foreground">Platform-wide activity tracking (Auto-deleted after 90 days)</p>
                    </div>
                </div>

                {/* Filters */}
                <Card>
                    <CardHeader>
                        <CardTitle>Filter Logs</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search logs..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                            <Select value={actionFilter} onValueChange={setActionFilter}>
                                <SelectTrigger className="w-[200px]">
                                    <SelectValue placeholder="All Actions" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Actions</SelectItem>
                                    {uniqueActions.map(action => (
                                        <SelectItem key={action} value={action}>
                                            {action.replace(/_/g, ' ').toUpperCase()}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button variant="outline" onClick={loadLogs}>
                                <Filter className="w-4 h-4 mr-2" />
                                Refresh
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold">{logs.length}</div>
                            <p className="text-xs text-muted-foreground">Total Logs</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold">
                                {logs.filter(l => l.action === 'admin_login').length}
                            </div>
                            <p className="text-xs text-muted-foreground">Admin Logins</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold">
                                {logs.filter(l => l.action.includes('staff')).length}
                            </div>
                            <p className="text-xs text-muted-foreground">Staff Changes</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold">
                                {logs.filter(l => l.action.includes('service')).length}
                            </div>
                            <p className="text-xs text-muted-foreground">Service Changes</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Logs List */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <CardTitle>Activity Timeline</CardTitle>
                            <p className="text-sm text-muted-foreground">
                                Showing {filteredLogs.length} of {logs.length} logs
                            </p>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {filteredLogs.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No activity logs found</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredLogs.map((log) => (
                                    <div key={log.id} className="flex items-start gap-4 p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                                        <div className="mt-1">
                                            {getActionIcon(log.action)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <p className="font-medium">{log.description}</p>
                                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                        <p className="text-sm text-muted-foreground">
                                                            by {log.admin_email}
                                                        </p>
                                                        {log.salon_name && (
                                                            <>
                                                                <span className="text-muted-foreground">•</span>
                                                                <p className="text-sm text-muted-foreground">
                                                                    {log.salon_name}
                                                                </p>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-2">
                                                    <Badge className={getActionColor(log.action)}>
                                                        {log.action.replace(/_/g, ' ')}
                                                    </Badge>
                                                    <p className="text-xs text-muted-foreground whitespace-nowrap">
                                                        {new Date(log.created_at).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Info Card */}
                <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                            <History className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div>
                                <p className="font-medium text-blue-900">Auto-Cleanup Information</p>
                                <p className="text-sm text-blue-800 mt-1">
                                    Activity logs are automatically deleted after 90 days to maintain system performance. 
                                    All actions including logins, staff changes, service modifications, salon updates, 
                                    and subscription changes are tracked.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}